/**
 * Service Worker Registration and Management
 */

export interface ServiceWorkerConfig {
  enabled?: boolean;
  scope?: string;
  updateCheckInterval?: number;
}

export class ServiceWorkerManager {
  private registration: ServiceWorkerRegistration | null = null;
  private config: ServiceWorkerConfig;

  constructor(config: ServiceWorkerConfig = {}) {
    this.config = {
      // Im Dev-Modus standardmäßig deaktivieren, um Fehler zu vermeiden
      enabled: typeof import.meta !== 'undefined' && (import.meta as any).env?.PROD ? true : false,
      scope: '/',
      updateCheckInterval: 1000 * 60 * 60, // 1 hour
      ...config,
    };
  }

  /**
   * Register service worker
   */
  async register(path: string = '/service-worker.js'): Promise<ServiceWorkerRegistration | null> {
    if (!this.config.enabled || !('serviceWorker' in navigator)) {
      console.warn('[SW] Service Workers not supported or disabled');
      return null;
    }

    try {
      this.registration = await navigator.serviceWorker.register(path, {
        scope: this.config.scope,
      });

      console.log('[SW] Registered successfully:', this.registration);

      // Check for updates periodically
      setInterval(() => this.checkForUpdates(), this.config.updateCheckInterval);

      return this.registration;
    } catch (error) {
      console.error('[SW] Registration failed:', error);
      return null;
    }
  }

  /**
   * Unregister service worker
   */
  async unregister(): Promise<boolean> {
    if (!this.registration) {
      return false;
    }

    try {
      const success = await this.registration.unregister();
      if (success) {
        console.log('[SW] Unregistered successfully');
        this.registration = null;
      }
      return success;
    } catch (error) {
      console.error('[SW] Unregister failed:', error);
      return false;
    }
  }

  /**
   * Check for updates
   */
  async checkForUpdates(): Promise<boolean> {
    if (!this.registration) {
      return false;
    }

    try {
      await this.registration.update();
      console.log('[SW] Checked for updates');

      if (this.registration.waiting) {
        console.log('[SW] New version available');
        return true;
      }

      return false;
    } catch (error) {
      console.error('[SW] Update check failed:', error);
      return false;
    }
  }

  /**
   * Clear cache
   */
  async clearCache(): Promise<void> {
    if (!('caches' in window)) {
      return;
    }

    try {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name)));
      console.log('[SW] Cache cleared');
    } catch (error) {
      console.error('[SW] Cache clear failed:', error);
    }
  }

  /**
   * Send message to service worker
   */
  async sendMessage(message: any): Promise<void> {
    if (!this.registration?.active) {
      return;
    }

    this.registration.active.postMessage(message);
  }

  /**
   * Get registration
   */
  getRegistration(): ServiceWorkerRegistration | null {
    return this.registration;
  }

  /**
   * Check if online
   */
  isOnline(): boolean {
    return navigator.onLine;
  }

  /**
   * Listen to online/offline events
   */
  onOnlineStatusChange(callback: (isOnline: boolean) => void): () => void {
    const handleOnline = () => callback(true);
    const handleOffline = () => callback(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }
}

// React Hook for service worker
export function useServiceWorker(config?: ServiceWorkerConfig) {
  const [manager] = React.useState(() => new ServiceWorkerManager(config));
  const [isOnline, setIsOnline] = React.useState(() => navigator.onLine);
  const [hasUpdate, setHasUpdate] = React.useState(false);

  React.useEffect(() => {
    // Register service worker nur wenn enabled
    if ((manager as any).config?.enabled) {
      manager.register();
    }

    // Listen to online/offline changes
    const unsubscribe = manager.onOnlineStatusChange((online) => {
      setIsOnline(online);
    });

    // Check for updates periodically
    const checkInterval = setInterval(async () => {
      if ((manager as any).config?.enabled) {
        const updateAvailable = await manager.checkForUpdates();
        setHasUpdate(updateAvailable);
      }
    }, 60000); // Check every minute

    return () => {
      unsubscribe();
      clearInterval(checkInterval);
    };
  }, [manager]);

  return {
    isOnline,
    hasUpdate,
    clearCache: () => manager.clearCache(),
    checkForUpdates: () => manager.checkForUpdates(),
  };
}

// Convenience helper to register a Service Worker directly
export async function registerServiceWorker(
  path: string = '/service-worker.js',
  config?: ServiceWorkerConfig
): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    console.warn('[SW] Service Workers not supported');
    return null;
  }
  const mgr = new ServiceWorkerManager(config);
  return mgr.register(path);
}

import React from 'react';
